/**
 * ProseMirror plugin that normalizes external pastes before BlockNote handles
 * them. Stable AgentWiki previously only intercepted markdown-like text/plain,
 * so rich HTML from Google Docs / Word / webpages still flowed through
 * BlockNote's default HTML paste path and could preserve foreign layout.
 *
 * NOTE: Uses editor._tiptapEditor (private API) for plugin registration.
 * May need updating on BlockNote major version upgrades.
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyBlockNoteEditor = import('@blocknote/core').BlockNoteEditor<any, any, any>
type ParsedBlocks = Awaited<ReturnType<AnyBlockNoteEditor['tryParseHTMLToBlocks']>>
import { sanitizeCodeFences } from '../../lib/sanitize-markdown-code-fences'

/** Matches actual code fence blocks: ```<lang>\n...\n``` (handles \r\n for Windows) */
const CODE_FENCE_REGEX = /```[\w-]*\r?\n[\s\S]*?\r?\n```/

/**
 * Matches common markdown block-level patterns at the start of any line.
 * Only triggers on multi-line text to avoid false positives on regular text.
 */
const MARKDOWN_BLOCK_REGEX =
  /^(#{1,6}\s|[-*+]\s|>\s|\d+\.\s|---|- \[[ x]\])/m

/**
 * Soft cap on inbound HTML payload. Above this we fall through to BlockNote's
 * default paste handler so we don't double-walk a multi-MB Word/Docs document
 * on the main thread.
 */
const MAX_HTML_PASTE_BYTES = 5 * 1024 * 1024

/**
 * BlockNote's HTMLToBlocks pathologically nests "loose list" HTML
 * (`<li><p>text</p></li>` — produced by GitHub-rendered markdown, Alibaba Cloud
 * docs, many static site generators). Each subsequent `<li>` becomes a child of
 * the previous one with text as a sibling paragraph, instead of flat siblings.
 *
 * Pre-flatten by unwrapping the lone `<p>` inside each `<li>` so the parser
 * sees the canonical tight-list shape (`<li>text</li>`). Preserves loose lists
 * with multiple block children (e.g. `<li><p>a</p><p>b</p></li>`) by only
 * unwrapping when there's exactly one paragraph child and no element siblings.
 */
export function unwrapSingleParagraphListItems(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('li').forEach((li) => {
    const elementChildren = Array.from(li.children)
    if (elementChildren.length !== 1) return
    const only = elementChildren[0]
    if (only.tagName !== 'P') return
    // Only unwrap when text/whitespace nodes outside the <p> are empty —
    // anything visible there is real content we shouldn't merge.
    for (const node of Array.from(li.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== '') return
    }
    while (only.firstChild) li.insertBefore(only.firstChild, only)
    only.remove()
  })
  return doc.body.innerHTML
}

/** Exported key so editor.tsx can properly unregister the plugin on cleanup */
export const pasteMarkdownPluginKey = new PluginKey('pasteMarkdownWithCodeBlocks')

export function shouldNormalizeMarkdownPaste(plainText: string): boolean {
  const hasCodeFence = CODE_FENCE_REGEX.test(plainText)
  const hasMarkdownBlocks = plainText.includes('\n') && MARKDOWN_BLOCK_REGEX.test(plainText)
  return hasCodeFence || hasMarkdownBlocks
}

export function shouldNormalizeHtmlPaste(clipboardData: Pick<DataTransfer, 'types' | 'getData'>): boolean {
  if (!clipboardData.types.includes('text/html')) return false
  if (clipboardData.types.includes('blocknote/html')) return false
  const html = clipboardData.getData('text/html')
  if (!html.trim()) return false
  // Above the soft cap, defer to BlockNote's default path.
  if (html.length > MAX_HTML_PASTE_BYTES) return false
  return true
}

function insertBlocksAtCursor(editor: AnyBlockNoteEditor, blocks: ParsedBlocks) {
  if (!blocks.length) return

  const cursorBlock = editor.getTextCursorPosition().block
  editor.insertBlocks(blocks, cursorBlock, 'after')

  // Remove the current block if it's empty (cursor was on blank line),
  // but only if it's not the last remaining block in the document.
  const currentContent = cursorBlock.content
  if (
    Array.isArray(currentContent) &&
    currentContent.length === 0 &&
    editor.document.length > 1
  ) {
    editor.removeBlocks([cursorBlock])
  }
}

function insertPlainTextFallback(editor: AnyBlockNoteEditor, text: string) {
  if (!text) return
  try {
    const cursorBlock = editor.getTextCursorPosition().block
    editor.insertBlocks(
      [{ type: 'paragraph', content: [{ type: 'text', text, styles: {} }] }],
      cursorBlock,
      'after',
    )
  } catch {
    // Editor may be unfocused — silently ignore.
  }
}

/**
 * Creates a ProseMirror plugin that intercepts external HTML or markdown-like
 * clipboard content and re-parses it into BlockNote blocks before insertion.
 *
 * Register after editor creation via editor._tiptapEditor.registerPlugin().
 */
export function createPasteMarkdownPlugin(editor: AnyBlockNoteEditor) {
  // Monotonic counter so a slow async parse from an earlier paste cannot
  // overwrite the result of a later paste. Stale resolutions are dropped.
  let pasteSeq = 0

  return new Plugin({
    key: pasteMarkdownPluginKey,
    props: {
      handleDOMEvents: {
        paste(_view: EditorView, event: Event) {
          if (!editor.isEditable) return false

          // When cursor is inside a code block, let ProseMirror handle paste natively
          // so text is inserted at the cursor position within the block
          const cursorBlock = editor.getTextCursorPosition().block
          if (cursorBlock.type === 'codeBlock') return false

          const clipboardEvent = event as ClipboardEvent
          const clipboardData = clipboardEvent.clipboardData
          if (!clipboardData) return false

          // Let VS Code's rich paste handler take priority
          if (clipboardData.types.includes('vscode-editor-data')) return false

          const plainText = clipboardData.getData('text/plain')
          const html = clipboardData.getData('text/html')

          if (shouldNormalizeHtmlPaste(clipboardData)) {
            clipboardEvent.preventDefault()
            const seq = ++pasteSeq
            const normalizedHtml = unwrapSingleParagraphListItems(html)
            editor.tryParseHTMLToBlocks(normalizedHtml).then((blocks) => {
              if (seq !== pasteSeq) return
              if (!blocks.length) {
                // Schema dropped every node (e.g., <style>-only payload, foreign
                // Office namespace tags). Restore user content via plain-text
                // fallback so the paste isn't silently swallowed.
                insertPlainTextFallback(editor, plainText)
                return
              }
              insertBlocksAtCursor(editor, blocks)
            }).catch(() => {
              if (seq !== pasteSeq) return
              // Don't log the parse error itself — prosemirror errors can
              // echo pasted attributes and leak content into shared consoles.
              console.warn('HTML paste parse failed; falling back to plain text')
              insertPlainTextFallback(editor, plainText)
            })
            return true
          }

          if (!plainText || !shouldNormalizeMarkdownPaste(plainText)) return false

          clipboardEvent.preventDefault()
          const seq = ++pasteSeq
          const safeMd = sanitizeCodeFences(plainText)

          editor.tryParseMarkdownToBlocks(safeMd).then((blocks) => {
            if (seq !== pasteSeq) return
            if (!blocks.length) {
              insertPlainTextFallback(editor, plainText)
              return
            }
            insertBlocksAtCursor(editor, blocks)
          }).catch(() => {
            if (seq !== pasteSeq) return
            console.warn('Markdown paste parse failed; falling back to plain text')
            insertPlainTextFallback(editor, plainText)
          })

          return true
        },
      },
    },
  })
}
